import type { z } from 'zod'

import * as animals from './animals'
import * as auth from './auth'
import * as expenses from './expenses'
import * as feed from './feed'
import * as lots from './lots'
import * as media from './media'
import * as owner from './owner'
import * as parties from './parties'
import * as platform from './platform'
import * as reports from './reports'
import * as sales from './sales'

/**
 * Every endpoint in plan §5, in one list.
 *
 * This is the single source for `ROUTES.md` (the page a frontend developer
 * opens) and for `openapi.ts` (the document the API is documented by). Adding
 * a route without an entry here means it appears in neither, which is the
 * point: the contract is what is written down, not what happens to be
 * implemented.
 *
 * `id` is the plan's own label (`A14`, `O3`, `P5`), so any row can be traced
 * back to the line that asked for it. Several plan rows bundle verbs — `A24`
 * is GET, PATCH and DELETE — and each verb gets its own entry under the same
 * id.
 */
export type AuthKind = 'none' | 'signupToken' | 'session' | 'platform'

export interface RouteDefinition {
  /** The plan §5 identifier: A1–A48, O1–O9, P1–P19. */
  readonly id: string
  readonly method: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  /** Path relative to `/api/v1`. */
  readonly path: string
  readonly summary: string
  readonly auth: AuthKind
  /** The role the server requires. Free text: some rows are conditional (C12). */
  readonly role: string
  /** Whether the request schema describes a JSON body, a query string, or a form. */
  readonly requestIn?: 'body' | 'query' | 'multipart'
  readonly request?: z.ZodType
  readonly requestName?: string
  readonly response?: z.ZodType
  readonly responseName?: string
  /** True when the response is a collection and carries `meta.nextCursor`. */
  readonly paginated?: boolean
  /** True when the response body is a stream of bytes rather than JSON. */
  readonly binary?: boolean
  readonly notes?: string
}

const route = (definition: RouteDefinition): RouteDefinition => definition

export const ROUTES: readonly RouteDefinition[] = [
  /* ── A1–A11 · auth, session, price lock ─────────────────────────────────── */
  route({
    id: 'A1', method: 'POST', path: '/auth/signup/start', summary: 'Begin self-signup — sends an OTP',
    auth: 'none', role: '—', requestIn: 'body',
    request: auth.signupStartRequest, requestName: 'SignupStartRequest',
    response: auth.otpChallengeResponse, responseName: 'OtpChallengeResponse',
    notes: 'Rate-limited per IP and per phone.',
  }),
  route({
    id: 'A2', method: 'POST', path: '/auth/signup/verify', summary: 'Confirm the OTP',
    auth: 'none', role: '—', requestIn: 'body',
    request: auth.signupVerifyRequest, requestName: 'SignupVerifyRequest',
    response: auth.signupVerifyResponse, responseName: 'SignupVerifyResponse',
  }),
  route({
    id: 'A3', method: 'POST', path: '/auth/signup/complete', summary: 'Create the book (screen 04)',
    auth: 'signupToken', role: '—', requestIn: 'body',
    request: auth.signupCompleteRequest, requestName: 'SignupCompleteRequest',
    response: auth.sessionResponse, responseName: 'SessionResponse',
    notes: 'Tenant, owner, TRIAL, feed plan and settings in one transaction.',
  }),
  route({
    id: 'A4', method: 'POST', path: '/auth/login', summary: 'Sign in (screen 03)',
    auth: 'none', role: '—', requestIn: 'body',
    request: auth.loginRequest, requestName: 'LoginRequest',
    response: auth.sessionResponse, responseName: 'SessionResponse',
  }),
  route({
    id: 'A5', method: 'POST', path: '/auth/logout', summary: 'Sign out',
    auth: 'session', role: 'any', response: auth.logoutResponse, responseName: 'LogoutResponse',
  }),
  route({
    id: 'A5', method: 'POST', path: '/auth/logout/all', summary: 'Sign out of every device',
    auth: 'session', role: 'any', response: auth.logoutAllResponse, responseName: 'LogoutAllResponse',
  }),
  route({
    id: 'A5', method: 'POST', path: '/auth/refresh', summary: 'Extend the session',
    auth: 'session', role: 'any', response: auth.refreshResponse, responseName: 'RefreshResponse',
  }),
  route({
    id: 'A6', method: 'POST', path: '/auth/password/forgot', summary: 'Start an OTP password reset',
    auth: 'none', role: '—', requestIn: 'body',
    request: auth.passwordForgotRequest, requestName: 'PasswordForgotRequest',
    response: auth.otpChallengeResponse, responseName: 'OtpChallengeResponse',
  }),
  route({
    id: 'A6', method: 'POST', path: '/auth/password/reset', summary: 'Finish the reset',
    auth: 'none', role: '—', requestIn: 'body',
    request: auth.passwordResetRequest, requestName: 'PasswordResetRequest',
    response: auth.passwordChangedResponse, responseName: 'PasswordChangedResponse',
    notes: 'Destroys every other session (SOW §7.5).',
  }),
  route({
    id: 'A7', method: 'POST', path: '/auth/password/change', summary: 'Change your own password',
    auth: 'session', role: 'any', requestIn: 'body',
    request: auth.passwordChangeRequest, requestName: 'PasswordChangeRequest',
    response: auth.passwordChangedResponse, responseName: 'PasswordChangedResponse',
  }),
  route({
    id: 'A8', method: 'GET', path: '/me', summary: 'Capabilities, plan, price lock, limits (§5.4)',
    auth: 'session', role: 'any', response: auth.meResponse, responseName: 'MeResponse',
  }),
  /* ── U1–U3 · his own account (CB-0005, beyond plan §5) ─────────────────── */
  route({
    id: 'U1', method: 'GET', path: '/account', summary: 'Personal, business and (Owner) subscription details',
    auth: 'session', role: 'any', response: auth.accountResponse, responseName: 'AccountResponse',
    notes: '`subscription` is absent for PARTNER and STAFF; its `pricePaisa` obeys the money gate.',
  }),
  route({
    id: 'U2', method: 'PATCH', path: '/account', summary: 'Change his own name — audited',
    auth: 'session', role: 'any', requestIn: 'body',
    request: auth.patchAccountRequest, requestName: 'PatchAccountRequest',
    response: auth.accountResponse, responseName: 'AccountResponse',
  }),
  route({
    id: 'U3', method: 'PATCH', path: '/account/business', summary: 'Rename the business, on every plan — audited',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: auth.patchAccountBusinessRequest, requestName: 'PatchAccountBusinessRequest',
    response: auth.accountResponse, responseName: 'AccountResponse',
  }),
  /* ── U4 · delete his own account (CB-0014, Apple 5.1.1(v)) ─────────────── */
  route({
    id: 'U4', method: 'POST', path: '/account/delete', summary: 'Delete his own account — the book too if he is its only Owner',
    auth: 'session', role: 'any', requestIn: 'body',
    request: auth.deleteAccountRequest, requestName: 'DeleteAccountRequest',
    response: auth.deleteAccountResponse, responseName: 'DeleteAccountResponse',
    notes: 'Allowed on a suspended or unpaid book. Password-checked; ends every session of the user.',
  }),
  route({
    id: 'A9', method: 'POST', path: '/price-lock/unlock', summary: 'PIN sheet (screen 08)',
    auth: 'session', role: 'OWNER · PARTNER', requestIn: 'body',
    request: auth.priceLockUnlockRequest, requestName: 'PriceLockUnlockRequest',
    response: auth.priceLockUnlockResponse, responseName: 'PriceLockUnlockResponse',
  }),
  route({
    id: 'A10', method: 'POST', path: '/price-lock/lock', summary: 'Hide prices now',
    auth: 'session', role: 'any',
    response: auth.priceLockLockResponse, responseName: 'PriceLockLockResponse',
  }),
  route({
    id: 'A11', method: 'POST', path: '/price-lock/pin', summary: 'Set or change the 4-digit PIN',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: auth.priceLockSetPinRequest, requestName: 'PriceLockSetPinRequest',
    response: auth.priceLockPinResponse, responseName: 'PriceLockPinResponse',
  }),

  /* ── A12–A21 · dashboard and animals ────────────────────────────────────── */
  route({
    id: 'A12', method: 'GET', path: '/dashboard', summary: 'Home tiles and attention list (screen 05)',
    auth: 'session', role: 'any',
    response: animals.dashboardResponse, responseName: 'DashboardResponse',
  }),
  route({
    id: 'A13', method: 'GET', path: '/animals', summary: 'The herd (screen 06)',
    auth: 'session', role: 'any', requestIn: 'query',
    request: animals.animalListQuery, requestName: 'AnimalListQuery',
    response: animals.animalListResponse, responseName: 'AnimalSummary[]', paginated: true,
    notes: 'sort ∈ name, ±daysHeld, teethIncisors, -breakEven*, -costBasis* (* money-gated).',
  }),
  route({
    id: 'A14', method: 'GET', path: '/animals/:id', summary: 'Animal detail (screens 07/09)',
    auth: 'session', role: 'any',
    response: animals.animalDetailResponse, responseName: 'AnimalDetail',
  }),
  route({
    id: 'A15', method: 'POST', path: '/animals', summary: 'Add an animal (screen 11)',
    auth: 'session', role: 'any', requestIn: 'body',
    request: animals.createAnimalRequest, requestName: 'CreateAnimalRequest',
    response: animals.animalDetailResponse, responseName: 'AnimalDetail',
    notes: 'Inline dalali/transport/mandi/vet become expenses in the same transaction.',
  }),
  route({
    id: 'A16', method: 'PATCH', path: '/animals/:id', summary: 'Edit an animal',
    auth: 'session', role: 'any (money fields need money)', requestIn: 'body',
    request: animals.patchAnimalRequest, requestName: 'PatchAnimalRequest',
    response: animals.animalDetailResponse, responseName: 'AnimalDetail',
  }),
  route({
    id: 'A17', method: 'DELETE', path: '/animals/:id', summary: 'Soft delete — 90-day trash',
    auth: 'session', role: 'OWNER',
    response: animals.softDeleteResponse, responseName: 'SoftDeleteResponse',
  }),
  route({
    id: 'A18', method: 'POST', path: '/animals/:id/restore', summary: 'Restore from trash',
    auth: 'session', role: 'OWNER',
    response: animals.restoreResponse, responseName: 'RestoreResponse',
  }),
  route({
    id: 'A19', method: 'GET', path: '/animals/backdate-preview', summary: 'What backdating will accrue (screen 11)',
    auth: 'session', role: 'money', requestIn: 'query',
    request: animals.backdatePreviewQuery, requestName: 'BackdatePreviewQuery',
    response: animals.backdatePreviewResponse, responseName: 'BackdatePreviewResponse',
    notes: 'Also served at /animals/:id/backdate-preview for an existing animal.',
  }),
  route({
    id: 'A20', method: 'GET', path: '/animals/:id/weights', summary: 'Weight log',
    auth: 'session', role: 'any',
    response: animals.weightListResponse, responseName: 'WeightEntry[]',
  }),
  route({
    id: 'A20', method: 'POST', path: '/animals/:id/weights', summary: 'Record a weighing',
    auth: 'session', role: 'any', requestIn: 'body',
    request: animals.createWeightRequest, requestName: 'CreateWeightRequest',
    response: animals.weightEntrySchema, responseName: 'WeightEntry',
  }),
  route({
    id: 'A21', method: 'POST', path: '/animals/:id/exit', summary: 'Death or loss (§4.11)',
    auth: 'session', role: 'OWNER · PARTNER', requestIn: 'body',
    request: animals.animalExitRequest, requestName: 'AnimalExitRequest',
    response: animals.animalExitResponse, responseName: 'AnimalExitResponse',
  }),

  /* ── A22–A26 · selling and payments ─────────────────────────────────────── */
  route({
    id: 'A22', method: 'GET', path: '/animals/:id/sale-quote', summary: 'What-if inputs (screen 10, §4.9)',
    auth: 'session', role: 'money',
    response: sales.saleQuoteResponse, responseName: 'SaleQuoteResponse',
    notes: '403 PRICE_LOCKED when locked. The one payload a client may compute on.',
  }),
  route({
    id: 'A23', method: 'POST', path: '/animals/:id/sale', summary: 'Confirm the sale',
    auth: 'session', role: 'money · Staff if staff_can_record_sale', requestIn: 'body',
    request: sales.createSaleRequest, requestName: 'CreateSaleRequest',
    response: sales.saleOutcomeSchema, responseName: 'SaleOutcome',
    notes: 'The server recomputes everything; its response is the source of truth.',
  }),
  route({
    id: 'A24', method: 'GET', path: '/sales/:id', summary: 'A sale',
    auth: 'session', role: 'money', response: sales.saleOutcomeSchema, responseName: 'SaleOutcome',
  }),
  route({
    id: 'A24', method: 'PATCH', path: '/sales/:id', summary: 'Correct a sale',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: sales.patchSaleRequest, requestName: 'PatchSaleRequest',
    response: sales.saleOutcomeSchema, responseName: 'SaleOutcome',
  }),
  route({
    id: 'A24', method: 'DELETE', path: '/sales/:id', summary: 'Void a sale',
    auth: 'session', role: 'OWNER',
    response: animals.softDeleteResponse, responseName: 'SoftDeleteResponse',
  }),
  route({
    id: 'A25', method: 'GET', path: '/sales/:id/payments', summary: 'Payments against a sale',
    auth: 'session', role: 'money',
    response: sales.paymentListResponse, responseName: 'Payment[]',
  }),
  route({
    id: 'A25', method: 'POST', path: '/sales/:id/payments', summary: 'Record a payment',
    auth: 'session', role: 'money', requestIn: 'body',
    request: sales.createPaymentRequest, requestName: 'CreatePaymentRequest',
    response: sales.paymentCreatedResponse, responseName: 'PaymentCreatedResponse',
    notes: '409 CONFLICT if it would exceed the sale price.',
  }),
  route({
    id: 'A26', method: 'GET', path: '/receivables', summary: 'Money owed and its ageing (screens 05, 15)',
    auth: 'session', role: 'money', requestIn: 'query',
    request: sales.receivableListQuery, requestName: 'ReceivableListQuery',
    response: sales.receivablesResponse, responseName: 'ReceivablesResponse', paginated: true,
  }),

  /* ── A27–A35 · expenses and monthly costs ───────────────────────────────── */
  route({
    id: 'A27', method: 'GET', path: '/expenses', summary: 'The month (screen 12)',
    auth: 'session', role: 'any', requestIn: 'query',
    request: expenses.expenseListQuery, requestName: 'ExpenseListQuery',
    response: expenses.expenseListResponse, responseName: 'ExpenseListResponse', paginated: true,
  }),
  route({
    id: 'A28', method: 'POST', path: '/expenses', summary: 'Add a cost (screen 23)',
    auth: 'session', role: 'any', requestIn: 'body',
    request: expenses.createExpenseRequest, requestName: 'CreateExpenseRequest',
    response: expenses.expenseSchema, responseName: 'Expense',
  }),
  route({
    id: 'A29', method: 'PATCH', path: '/expenses/:id', summary: 'Edit a cost',
    auth: 'session', role: 'OWNER · PARTNER', requestIn: 'body',
    request: expenses.patchExpenseRequest, requestName: 'PatchExpenseRequest',
    response: expenses.expenseSchema, responseName: 'Expense',
  }),
  route({
    id: 'A29', method: 'DELETE', path: '/expenses/:id', summary: 'Void a cost',
    auth: 'session', role: 'OWNER · PARTNER',
    response: animals.softDeleteResponse, responseName: 'SoftDeleteResponse',
  }),
  route({
    id: 'A30', method: 'GET', path: '/expenses/reality-check', summary: 'Assumed vs actual daily rate (§4.15)',
    auth: 'session', role: 'money', requestIn: 'query',
    request: expenses.realityCheckQuery, requestName: 'RealityCheckQuery',
    response: expenses.realityCheckResponse, responseName: 'RealityCheckResponse',
  }),
  route({
    id: 'A31', method: 'GET', path: '/recurring-expenses', summary: 'Monthly costs and this month’s postings (screen 13)',
    auth: 'session', role: 'money', requestIn: 'query',
    request: expenses.recurringListQuery, requestName: 'RecurringListQuery',
    response: expenses.recurringListResponse, responseName: 'RecurringListResponse',
  }),
  route({
    id: 'A32', method: 'POST', path: '/recurring-expenses', summary: 'Add a monthly cost (screen 14)',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: expenses.createRecurringExpenseRequest, requestName: 'CreateRecurringExpenseRequest',
    response: expenses.recurringExpenseSchema, responseName: 'RecurringExpense',
  }),
  route({
    id: 'A33', method: 'PATCH', path: '/recurring-expenses/:id', summary: 'Edit a monthly cost',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: expenses.patchRecurringExpenseRequest, requestName: 'PatchRecurringExpenseRequest',
    response: expenses.recurringExpenseSchema, responseName: 'RecurringExpense',
  }),
  route({
    id: 'A33', method: 'DELETE', path: '/recurring-expenses/:id', summary: 'Stop a monthly cost',
    auth: 'session', role: 'OWNER',
    response: animals.softDeleteResponse, responseName: 'SoftDeleteResponse',
  }),
  route({
    id: 'A34', method: 'POST', path: '/recurring-postings/:id/confirm', summary: 'Confirm this month’s wanda',
    auth: 'session', role: 'OWNER · PARTNER', requestIn: 'body',
    request: expenses.confirmPostingRequest, requestName: 'ConfirmPostingRequest',
    response: expenses.postingResultResponse, responseName: 'PostingResultResponse',
    notes: 'Writes the expense and triggers §4.3 feed suppression.',
  }),
  route({
    id: 'A35', method: 'POST', path: '/recurring-postings/:id/skip', summary: 'Skip this month',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: expenses.skipPostingRequest, requestName: 'SkipPostingRequest',
    response: expenses.postingResultResponse, responseName: 'PostingResultResponse',
  }),

  /* ── A36 · feed ─────────────────────────────────────────────────────────── */
  route({
    id: 'A36', method: 'GET', path: '/feed-plans', summary: 'Daily rates over date ranges',
    auth: 'session', role: 'OWNER', requestIn: 'query',
    request: feed.feedPlanListQuery, requestName: 'FeedPlanListQuery',
    response: feed.feedPlanListResponse, responseName: 'FeedPlan[]',
  }),
  route({
    id: 'A36', method: 'POST', path: '/feed-plans', summary: 'Set a rate',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: feed.createFeedPlanRequest, requestName: 'CreateFeedPlanRequest',
    response: feed.feedPlanSchema, responseName: 'FeedPlan',
  }),
  route({
    id: 'A36', method: 'PATCH', path: '/feed-plans/:id', summary: 'Correct a rate — retroactive by construction',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: feed.patchFeedPlanRequest, requestName: 'PatchFeedPlanRequest',
    response: feed.feedPlanSchema, responseName: 'FeedPlan',
  }),
  route({
    id: 'A36', method: 'DELETE', path: '/feed-plans/:id', summary: 'Remove a rate',
    auth: 'session', role: 'OWNER',
    response: animals.softDeleteResponse, responseName: 'SoftDeleteResponse',
  }),

  /* ── A37–A39 · lots ─────────────────────────────────────────────────────── */
  route({
    id: 'A37', method: 'POST', path: '/lots', summary: 'Add a lot — animals and split costs (screen 24)',
    auth: 'session', role: 'any', requestIn: 'body',
    request: lots.createLotRequest, requestName: 'CreateLotRequest',
    response: lots.lotDetailResponse, responseName: 'LotDetail',
    notes: 'One transaction: lot + N animals + derived expense rows (§4.13).',
  }),
  route({
    id: 'A38', method: 'GET', path: '/lots', summary: 'Lots',
    auth: 'session', role: 'any', requestIn: 'query',
    request: lots.lotListQuery, requestName: 'LotListQuery',
    response: lots.lotListResponse, responseName: 'LotSummary[]', paginated: true,
  }),
  route({
    id: 'A38', method: 'GET', path: '/lots/:id', summary: 'Lot detail with the split explained (screen 25)',
    auth: 'session', role: 'any',
    response: lots.lotDetailResponse, responseName: 'LotDetail',
  }),
  route({
    id: 'A39', method: 'PATCH', path: '/lots/:id/costs/:costId', summary: 'Change a split — re-derives its rows',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: lots.patchLotCostRequest, requestName: 'PatchLotCostRequest',
    response: lots.lotDetailResponse, responseName: 'LotDetail',
  }),

  /* ── A40–A42 · media ────────────────────────────────────────────────────── */
  route({
    id: 'A40', method: 'POST', path: '/media/upload', summary: 'Upload a photo or video',
    auth: 'session', role: 'any', requestIn: 'multipart',
    request: media.mediaUploadFields, requestName: 'MediaUploadFields',
    response: media.mediaUploadResponse, responseName: 'MediaItem',
    notes: 'Streamed, magic-byte sniffed. 413, 415, 409 storage quota.',
  }),
  route({
    id: 'A41', method: 'GET', path: '/media/:id/:variant', summary: 'Serve a rendition',
    auth: 'session', role: 'any', binary: true,
    notes: '206 Range mandatory, ETag, immutable cache. Never a public URL.',
  }),
  route({
    id: 'A42', method: 'PATCH', path: '/media/:id', summary: 'Cover, caption, order',
    auth: 'session', role: 'any', requestIn: 'body',
    request: media.mediaPatchRequest, requestName: 'MediaPatchRequest',
    response: media.mediaItemSchema, responseName: 'MediaItem',
  }),
  route({
    id: 'A42', method: 'DELETE', path: '/media/:id', summary: 'Delete media',
    auth: 'session', role: 'any',
    response: animals.softDeleteResponse, responseName: 'SoftDeleteResponse',
  }),

  /* ── A43–A47 · reports ──────────────────────────────────────────────────── */
  route({
    id: 'A43', method: 'GET', path: '/reports/summary', summary: 'Booked vs cash P&L (§4.19)',
    auth: 'session', role: 'money + REPORTS', requestIn: 'query',
    request: reports.reportRangeQuery, requestName: 'ReportRangeQuery',
    response: reports.reportSummaryResponse, responseName: 'ReportSummaryResponse',
    notes: 'Both numbers, both labelled, plus the one-line explanation.',
  }),
  route({
    id: 'A43', method: 'GET', path: '/reports/activity', summary: 'The month in counts — no money',
    auth: 'session', role: 'any', requestIn: 'query',
    request: reports.reportRangeQuery, requestName: 'ReportRangeQuery',
    response: reports.reportActivityResponse, responseName: 'ReportActivityResponse',
    notes: 'The Staff Reports tab. Carries no paisa field at all; REPORTS plan feature.',
  }),
  route({
    id: 'A44', method: 'GET', path: '/reports/per-animal', summary: 'Ranked by profit per day',
    auth: 'session', role: 'money + REPORTS', requestIn: 'query',
    request: reports.perAnimalReportQuery, requestName: 'PerAnimalReportQuery',
    response: reports.perAnimalReportResponse, responseName: 'PerAnimalReportRow[]', paginated: true,
  }),
  route({
    id: 'A45', method: 'GET', path: '/reports/cost-breakdown', summary: 'Where the money goes',
    auth: 'session', role: 'money + REPORTS', requestIn: 'query',
    request: reports.reportRangeQuery, requestName: 'ReportRangeQuery',
    response: reports.costBreakdownReportResponse, responseName: 'CostBreakdownReportResponse',
  }),
  route({
    id: 'A46', method: 'GET', path: '/reports/ageing', summary: 'Inventory and receivable ageing',
    auth: 'session', role: 'money + REPORTS', requestIn: 'query',
    request: reports.reportRangeQuery, requestName: 'ReportRangeQuery',
    response: reports.ageingReportResponse, responseName: 'AgeingReportResponse',
  }),
  route({
    id: 'A47', method: 'GET', path: '/reports/export', summary: 'Export the book',
    auth: 'session', role: 'money + REPORTS', requestIn: 'query', binary: true,
    request: reports.reportExportQuery, requestName: 'ReportExportQuery',
    notes: 'Streams xlsx or csv. Audited.',
  }),

  /* ── A48 · parties ──────────────────────────────────────────────────────── */
  route({
    id: 'A48', method: 'GET', path: '/parties', summary: 'Buyers, sellers, dalals — the typeahead',
    auth: 'session', role: 'any', requestIn: 'query',
    request: parties.partyListQuery, requestName: 'PartyListQuery',
    response: parties.partyListResponse, responseName: 'Party[]', paginated: true,
  }),
  route({
    id: 'A48', method: 'POST', path: '/parties', summary: 'Add a party',
    auth: 'session', role: 'any', requestIn: 'body',
    request: parties.createPartyRequest, requestName: 'CreatePartyRequest',
    response: parties.partySchema, responseName: 'Party',
  }),
  route({
    id: 'A48', method: 'PATCH', path: '/parties/:id', summary: 'Edit a party',
    auth: 'session', role: 'any', requestIn: 'body',
    request: parties.patchPartyRequest, requestName: 'PatchPartyRequest',
    response: parties.partySchema, responseName: 'Party',
  }),

  /* ── O1–O9 · owner console ──────────────────────────────────────────────── */
  route({
    id: 'O1', method: 'GET', path: '/people', summary: 'People (design 02)',
    auth: 'session', role: 'OWNER',
    response: owner.peopleListResponse, responseName: 'Person[]',
  }),
  route({
    id: 'O1', method: 'POST', path: '/people', summary: 'Add a person',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: owner.createPersonRequest, requestName: 'CreatePersonRequest',
    response: owner.personSchema, responseName: 'Person',
  }),
  route({
    id: 'O1', method: 'PATCH', path: '/people/:id', summary: 'Change a name or role',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: owner.patchPersonRequest, requestName: 'PatchPersonRequest',
    response: owner.personSchema, responseName: 'Person',
  }),
  route({
    id: 'O1', method: 'POST', path: '/people/:id/deactivate', summary: 'Deactivate — never delete',
    auth: 'session', role: 'OWNER',
    response: owner.deactivatePersonResponse, responseName: 'DeactivatePersonResponse',
  }),
  route({
    id: 'O2', method: 'POST', path: '/people/:id/reset-password', summary: 'One-time password, shown once (design 03)',
    auth: 'session', role: 'OWNER',
    response: owner.resetPasswordResponse, responseName: 'ResetPasswordResponse',
  }),
  route({
    id: 'O3', method: 'GET', path: '/settings', summary: 'Rates, commissions, price lock (design 06)',
    auth: 'session', role: 'OWNER',
    response: owner.tenantSettingsResponse, responseName: 'TenantSettingsResponse',
  }),
  route({
    id: 'O3', method: 'PATCH', path: '/settings', summary: 'Change an assumption — retroactive, audited',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: owner.patchTenantSettingsRequest, requestName: 'PatchTenantSettingsRequest',
    response: owner.tenantSettingsResponse, responseName: 'TenantSettingsResponse',
  }),
  route({
    id: 'O4', method: 'GET', path: '/audit-log', summary: 'Was → Now activity (design 04)',
    auth: 'session', role: 'OWNER', requestIn: 'query',
    request: owner.auditLogQuery, requestName: 'AuditLogQuery',
    response: owner.auditLogResponse, responseName: 'AuditEntry[]', paginated: true,
  }),
  route({
    id: 'O5', method: 'GET', path: '/trash', summary: '90-day trash',
    auth: 'session', role: 'OWNER', requestIn: 'query',
    request: owner.trashListQuery, requestName: 'TrashListQuery',
    response: owner.trashListResponse, responseName: 'TrashItem[]', paginated: true,
  }),
  route({
    id: 'O5', method: 'POST', path: '/trash/:entity/:id/restore', summary: 'Restore a deleted row',
    auth: 'session', role: 'OWNER',
    response: owner.trashRestoreResponse, responseName: 'TrashRestoreResponse',
  }),
  route({
    id: 'O6', method: 'POST', path: '/backups', summary: 'Take a backup',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: owner.createBackupRequest, requestName: 'CreateBackupRequest',
    response: owner.backupSchema, responseName: 'Backup',
  }),
  route({
    id: 'O7', method: 'GET', path: '/backups', summary: 'Backups and the nightly state',
    auth: 'session', role: 'OWNER',
    response: owner.backupListResponse, responseName: 'BackupListResponse',
  }),
  route({
    id: 'O7', method: 'GET', path: '/backups/:id/download', summary: 'Signed, expiring link',
    auth: 'session', role: 'OWNER',
    response: owner.backupDownloadResponse, responseName: 'BackupDownloadResponse',
  }),
  route({
    id: 'O8', method: 'POST', path: '/backups/:id/restore', summary: 'Restore — snapshots first',
    auth: 'session', role: 'OWNER', requestIn: 'body',
    request: owner.restoreBackupRequest, requestName: 'RestoreBackupRequest',
    response: owner.restoreBackupResponse, responseName: 'RestoreBackupResponse',
  }),
  route({
    id: 'O9', method: 'GET', path: '/storage-usage', summary: 'Storage breakdown (design 05)',
    auth: 'session', role: 'OWNER',
    response: owner.storageUsageResponse, responseName: 'StorageUsageResponse',
  }),

  /* ── P1–P19 · platform console ──────────────────────────────────────────── */
  route({
    id: 'P0', method: 'POST', path: '/platform/auth/login', summary: 'Platform sign-in (§4.20)',
    auth: 'none', role: '—', requestIn: 'body',
    request: platform.platformLoginRequest, requestName: 'PlatformLoginRequest',
    response: platform.platformSessionResponse, responseName: 'PlatformSessionResponse',
    notes: 'No tenantSlug: checked against tenant_id IS NULL only.',
  }),
  route({
    id: 'P1', method: 'GET', path: '/platform/overview', summary: 'Screen 01',
    auth: 'platform', role: 'both',
    response: platform.platformOverviewResponse, responseName: 'PlatformOverviewResponse',
  }),
  route({
    id: 'P2', method: 'GET', path: '/platform/businesses', summary: 'Screen 02',
    auth: 'platform', role: 'both', requestIn: 'query',
    request: platform.businessListQuery, requestName: 'BusinessListQuery',
    response: platform.businessListResponse, responseName: 'BusinessSummary[]', paginated: true,
  }),
  route({
    id: 'P3', method: 'POST', path: '/platform/businesses', summary: 'Add a business',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.createBusinessRequest, requestName: 'CreateBusinessRequest',
    response: platform.createBusinessResponse, responseName: 'CreateBusinessResponse',
  }),
  route({
    id: 'P4', method: 'GET', path: '/platform/businesses/:id', summary: 'Screen 03',
    auth: 'platform', role: 'both',
    response: platform.businessDetailResponse, responseName: 'BusinessDetailResponse',
  }),
  /*
   * A CONTRACT ADDITION made on 10 September 2026, after the T3.6 freeze.
   *
   * It carries id `P4` rather than a new one because the plan DID ask for it:
   * §5.7 P4 says screen 03 shows "usage, subscription, INVOICES, people,
   * health", and §6.1 P-03 renders an `InvoiceTable`. But
   * `businessDetailResponse` carries no invoice list and no route served one, so
   * the table had nothing to read. Splitting the sub-resource onto its own path
   * keeps the detail payload from growing an unbounded array; the id stays P4
   * because that is the plan row this satisfies, and `registry.test.ts` is right
   * to refuse an id the plan never mentioned.
   *
   * Nothing new is designed here: `invoiceSchema` already existed and
   * `invoicesRepo.listInvoicesForTenant()` was already written and unused,
   * waiting for exactly this.
   *
   * `role: 'both'` follows P2/P4/P10 — support may read a business, and the
   * money keys are removed from the payload for an actor without
   * `platform.billing` by the serializer, not by hiding the route.
   */
  route({
    id: 'P4', method: 'GET', path: '/platform/businesses/:id/invoices', summary: 'Screen 03 · invoice table',
    auth: 'platform', role: 'both',
    response: platform.invoiceListResponse, responseName: 'InvoiceListResponse',
  }),
  route({
    id: 'P5', method: 'POST', path: '/platform/businesses/:id/access', summary: 'Open their book — reason required',
    auth: 'platform', role: 'both', requestIn: 'body',
    request: platform.openAccessRequest, requestName: 'OpenAccessRequest',
    response: platform.accessGrantResponse, responseName: 'AccessGrantResponse',
    notes: 'Writes an immutable access_log row first. Scoped session, 1 hour.',
  }),
  route({
    id: 'P6', method: 'POST', path: '/platform/access/:accessId/mode', summary: 'Switch to edit (screen 06)',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.changeAccessModeRequest, requestName: 'ChangeAccessModeRequest',
    response: platform.accessGrantResponse, responseName: 'AccessGrantResponse',
  }),
  route({
    id: 'P7', method: 'POST', path: '/platform/access/:accessId/close', summary: 'Leave the book',
    auth: 'platform', role: 'both',
    response: platform.closeAccessResponse, responseName: 'CloseAccessResponse',
  }),
  route({
    id: 'P8', method: 'POST', path: '/platform/businesses/:id/trial/extend', summary: 'Extend a trial',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.extendTrialRequest, requestName: 'ExtendTrialRequest',
    response: platform.businessStatusResponse, responseName: 'BusinessStatusResponse',
  }),
  route({
    id: 'P8', method: 'POST', path: '/platform/businesses/:id/suspend', summary: 'Suspend — sign-in blocked',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.changeTenantStatusRequest, requestName: 'ChangeTenantStatusRequest',
    response: platform.businessStatusResponse, responseName: 'BusinessStatusResponse',
  }),
  route({
    id: 'P8', method: 'POST', path: '/platform/businesses/:id/read-only', summary: 'Read-only — GETs still work',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.changeTenantStatusRequest, requestName: 'ChangeTenantStatusRequest',
    response: platform.businessStatusResponse, responseName: 'BusinessStatusResponse',
  }),
  route({
    id: 'P8', method: 'DELETE', path: '/platform/businesses/:id', summary: 'Delete a business',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.deleteBusinessRequest, requestName: 'DeleteBusinessRequest',
    response: platform.businessStatusResponse, responseName: 'BusinessStatusResponse',
  }),
  route({
    id: 'P9', method: 'GET', path: '/platform/plans', summary: 'Screen 04',
    auth: 'platform', role: 'PLATFORM_OWNER',
    response: platform.planListResponse, responseName: 'Plan[]',
  }),
  route({
    id: 'P9', method: 'POST', path: '/platform/plans', summary: 'Add a plan',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.createPlanRequest, requestName: 'CreatePlanRequest',
    response: platform.planSchema, responseName: 'Plan',
  }),
  route({
    id: 'P9', method: 'PATCH', path: '/platform/plans/:id', summary: 'Change a plan',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.patchPlanRequest, requestName: 'PatchPlanRequest',
    response: platform.planSchema, responseName: 'Plan',
    notes: 'Every field, code included. A code must still be one of PLAN_CODES.',
  }),
  route({
    id: 'P9', method: 'DELETE', path: '/platform/plans/:id', summary: 'Retire a plan',
    auth: 'platform', role: 'PLATFORM_OWNER',
    notes:
      '204, and a soft delete — invoices and cancelled subscriptions still point at the row. ' +
      'CONFLICT with reason PLAN_IN_USE and a count when any live book is on it; ' +
      'isPublic: false is how a plan in use is taken off signup.',
  }),
  /*
   * A CONTRACT ADDITION made on 10 September 2026, after the T3.6 freeze, on
   * the P4 precedent above and for the same reason.
   *
   * It carries id `P9` because that is the plan row it satisfies: §6.1's P-05
   * lists `PaymentMixList` among screen 04's components, and
   * `paymentMixResponse` was written for exactly that tile — but no route ever
   * served it, so the console filled the tile from
   * `platformSettingsResponse.gateways`, which counts REGISTERED methods and
   * knows nothing about what was actually collected on each.
   * `registry.test.ts` is right to refuse an id the plan never named.
   *
   * `role: 'both'`, not the `PLATFORM_OWNER` of the three P9 routes above.
   * Those return `Plan`, where `pricePaisa` and `revenuePaisa` are the
   * platform's revenue and nothing else. This row is two facts side by side —
   * which rail, and how much has arrived on it — and only the second is money,
   * so the route stays open and `insights.service.ts` deletes `amountPaisa`
   * from the payload for an actor without `platform.billing`, exactly as the
   * P4 row above does. Refusing the route instead would leave support unable
   * to answer "can he pay by bank transfer", which is a support question and
   * not a commercial one.
   */
  route({
    id: 'P9', method: 'GET', path: '/platform/payment-mix', summary: 'Screen 04 · how they pay',
    auth: 'platform', role: 'both',
    response: platform.paymentMixResponse, responseName: 'PaymentMixResponse',
  }),
  route({
    id: 'P10', method: 'GET', path: '/platform/subscriptions', summary: 'Screen 07',
    auth: 'platform', role: 'both', requestIn: 'query',
    request: platform.subscriptionListQuery, requestName: 'SubscriptionListQuery',
    response: platform.subscriptionsSummaryResponse, responseName: 'SubscriptionsSummaryResponse',
  }),
  route({
    id: 'P11', method: 'POST', path: '/platform/invoices/:id/record-payment', summary: 'Manual confirmation (D3)',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.recordInvoicePaymentRequest, requestName: 'RecordInvoicePaymentRequest',
    response: platform.invoiceSchema, responseName: 'Invoice',
  }),
  route({
    id: 'P12', method: 'POST', path: '/platform/invoices/:id/retry', summary: 'Retry the charge',
    auth: 'platform', role: 'PLATFORM_OWNER',
    response: platform.invoiceSchema, responseName: 'Invoice',
  }),
  route({
    id: 'P12', method: 'POST', path: '/platform/invoices/:id/remind', summary: 'Send a reminder',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.remindInvoiceRequest, requestName: 'RemindInvoiceRequest',
    response: platform.invoiceSchema, responseName: 'Invoice',
  }),
  route({
    id: 'P13', method: 'GET', path: '/platform/usage', summary: 'Screen 08 — near-limit rows and the lever',
    auth: 'platform', role: 'both', requestIn: 'query',
    request: platform.usageQuery, requestName: 'UsageQuery',
    response: platform.usageSummaryResponse, responseName: 'UsageSummaryResponse', paginated: true,
  }),
  route({
    id: 'P14', method: 'GET', path: '/platform/health', summary: 'Screen 05 — backups, video queue, failures',
    auth: 'platform', role: 'both',
    response: platform.healthSummaryResponse, responseName: 'HealthSummaryResponse',
  }),
  /*
   * A CONTRACT ADDITION made on 10 September 2026, under the existing id P14
   * because §6.1's P-08 row already asked for it — "Notify owner (G22) — both"
   * — and no route ever served it. `broadcastRequest` cannot: its audience is a
   * filter with no way to name a tenant, so the console had removed the control
   * rather than send one man a message by choosing a filter that matched only
   * him. `role: 'both'` follows that same row.
   */
  route({
    id: 'P14', method: 'POST', path: '/platform/businesses/:id/notify', summary: 'Screen 05 · tell one owner',
    auth: 'platform', role: 'both', requestIn: 'body',
    request: platform.notifyOwnerRequest, requestName: 'NotifyOwnerRequest',
    response: platform.notifyOwnerResponse, responseName: 'NotifyOwnerResponse',
  }),
  route({
    id: 'P15', method: 'GET', path: '/platform/support/tickets', summary: 'Screen 09',
    auth: 'platform', role: 'both', requestIn: 'query',
    request: platform.ticketListQuery, requestName: 'TicketListQuery',
    response: platform.ticketListResponse, responseName: 'Ticket[]', paginated: true,
  }),
  route({
    id: 'P15', method: 'POST', path: '/platform/support/tickets', summary: 'Log a ticket',
    auth: 'platform', role: 'both', requestIn: 'body',
    request: platform.createTicketRequest, requestName: 'CreateTicketRequest',
    response: platform.ticketSchema, responseName: 'Ticket',
  }),
  route({
    id: 'P15', method: 'PATCH', path: '/platform/support/tickets/:id', summary: 'Assign, categorise, close',
    auth: 'platform', role: 'both', requestIn: 'body',
    request: platform.patchTicketRequest, requestName: 'PatchTicketRequest',
    response: platform.ticketSchema, responseName: 'Ticket',
  }),
  route({
    id: 'P15', method: 'GET', path: '/platform/support/tickets/:id/messages', summary: 'The conversation',
    auth: 'platform', role: 'both',
    response: platform.ticketMessageListResponse, responseName: 'TicketMessage[]',
  }),
  route({
    id: 'P15', method: 'POST', path: '/platform/support/tickets/:id/messages', summary: 'Reply',
    auth: 'platform', role: 'both', requestIn: 'body',
    request: platform.createTicketMessageRequest, requestName: 'CreateTicketMessageRequest',
    response: platform.ticketMessageSchema, responseName: 'TicketMessage',
  }),
  /*
   * A CONTRACT ADDITION made on 10 September 2026, on the same P4 precedent.
   *
   * `askedAboutResponse` was written for §6.1 P-09's `AskedAboutBars` and no
   * route served it, so the console counted categories out of ONE PAGE of
   * `GET /platform/support/tickets` — arithmetic over a cursor-paged list,
   * which is a sample and not a total, and quietly wrong from the second page
   * on. This is one grouped count over every ticket.
   *
   * `role: 'both'` and `platform.support`, following every P15 row above: it
   * is the same screen, and support is who reads it. No money is in the
   * response at all.
   */
  route({
    id: 'P15', method: 'GET', path: '/platform/support/asked-about', summary: 'Screen 09 · what they ask about',
    auth: 'platform', role: 'both',
    response: platform.askedAboutResponse, responseName: 'AskedAboutResponse',
  }),
  route({
    id: 'P16', method: 'GET', path: '/platform/access-log', summary: 'Screen 10 — read-only, no mutating verb exists',
    auth: 'platform', role: 'both', requestIn: 'query',
    request: platform.accessLogQuery, requestName: 'AccessLogQuery',
    response: platform.accessLogResponse, responseName: 'AccessLogResponse', paginated: true,
  }),
  route({
    id: 'P17', method: 'GET', path: '/platform/settings', summary: 'Screen 11',
    auth: 'platform', role: 'PLATFORM_OWNER',
    response: platform.platformSettingsResponse, responseName: 'PlatformSettingsResponse',
  }),
  route({
    id: 'P17', method: 'PATCH', path: '/platform/settings', summary: 'Trials, limits, backup hour, maintenance',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.patchPlatformSettingsRequest, requestName: 'PatchPlatformSettingsRequest',
    response: platform.platformSettingsResponse, responseName: 'PlatformSettingsResponse',
  }),
  route({
    id: 'P18', method: 'GET', path: '/platform/team', summary: 'Screen 11 team table',
    auth: 'platform', role: 'PLATFORM_OWNER',
    response: platform.teamListResponse, responseName: 'TeamMember[]',
  }),
  route({
    id: 'P18', method: 'POST', path: '/platform/team', summary: 'Add a platform user',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.createTeamMemberRequest, requestName: 'CreateTeamMemberRequest',
    response: platform.createTeamMemberResponse, responseName: 'CreateTeamMemberResponse',
  }),
  route({
    id: 'P19', method: 'POST', path: '/platform/broadcast', summary: 'Message every owner (G22 — no provider decided)',
    auth: 'platform', role: 'PLATFORM_OWNER', requestIn: 'body',
    request: platform.broadcastRequest, requestName: 'BroadcastRequest',
    response: platform.broadcastResponse, responseName: 'BroadcastResponse',
  }),
]

/** Every plan §5 id the registry implements, in plan order. */
export const IMPLEMENTED_ROUTE_IDS: readonly string[] = Array.from(
  new Set(ROUTES.map((r) => r.id)),
)
